import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiKeyToken, ApiKeyAvailableEndpointToken } from '@sleed/bizcommerce-api';
import { apiKeysApi } from '~/lib/api/client';
import { formToCreateRequest, formToUpdateRequest, type ApiKeyFormValues } from '~/lib/schemas/api-key';

export const apiKeyKeys = {
	all: ['api-keys'] as const,
	list: () => [...apiKeyKeys.all, 'list'] as const,
	endpoints: () => [...apiKeyKeys.all, 'endpoints'] as const
};

export function useApiKeysList() {
	return useQuery<ApiKeyToken[]>({
		queryKey: apiKeyKeys.list(),
		queryFn: async () => (await apiKeysApi.getAll()).data.items ?? []
	});
}

/** The endpoints an API key can be scoped to. */
export function useApiKeyEndpoints() {
	return useQuery<ApiKeyAvailableEndpointToken[]>({
		queryKey: apiKeyKeys.endpoints(),
		queryFn: async () => (await apiKeysApi.availableEndpoints()).data.items ?? [],
		staleTime: 10 * 60 * 1000
	});
}

export function useApiKeyMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: apiKeyKeys.list() });

	/** Returns the minted response — the one-time `token` must be shown to the user once. */
	const create = useMutation({
		mutationFn: async (form: ApiKeyFormValues) => (await apiKeysApi.insert(formToCreateRequest(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: ApiKeyFormValues) => (await apiKeysApi.update(formToUpdateRequest(form), form.id)).data,
		onSuccess: invalidate
	});
	/** Rotates the secret — also returns a one-time `token`. */
	const regenerate = useMutation({
		mutationFn: async (id: number) => (await apiKeysApi.regenerate(id)).data,
		onSuccess: invalidate
	});
	const revoke = useMutation({ mutationFn: async (id: number) => (await apiKeysApi.revoke(id)).data, onSuccess: invalidate });
	const remove = useMutation({ mutationFn: async (id: number) => (await apiKeysApi.remove(id)).data, onSuccess: invalidate });

	return { create, update, regenerate, revoke, remove };
}
